import classNames from 'clsx';
import React, { forwardRef } from 'react';
import type { TableBodyProps as TableBodyPropsBase } from 'react-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import Loading from '../../Loading/Loading';
import TableEmptyMessage from '../TableEmptyMessage/TableEmptyMessage';
import type { ItemData } from '../TableRow/TableRow';
import TableRow from '../TableRow/TableRow';

import styles from './TableBody.scss';

type ListProps = Omit<React.ComponentProps<typeof FixedSizeList<ItemData>>, 'children'> & {
  fixedListRef?: React.Ref<FixedSizeList>;
};

interface ListWrapperProps extends ListProps {
  itemSize: number;
  autoSizer?: boolean;
}

interface TableBodyProps extends ListWrapperProps {
  bodyProps?: TableBodyPropsBase;
  loading: boolean;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

const List: React.FC<ListProps> = ({
  height,
  itemCount,
  itemData,
  itemSize,
  width,
  fixedListRef,
  outerElementType
}) => (
  <FixedSizeList
    ref={fixedListRef}
    height={height}
    itemCount={itemCount}
    itemData={itemData}
    itemSize={itemSize}
    width={width}
    outerElementType={outerElementType}
  >
    {TableRow}
  </FixedSizeList>
);

const ListWrapper: React.FC<ListWrapperProps> = ({ autoSizer, ...rest }) =>
  autoSizer ? (
    <AutoSizer>
      {({ height, width }: { height: number; width: number }) => (
        <List {...rest} height={height} width={width} />
      )}
    </AutoSizer>
  ) : (
    <List {...rest} />
  );

const TableBody: React.FC<TableBodyProps> = forwardRef(
  ({ bodyProps = {}, loading, className, ...rest }, ref) => {
    const ListComponent = rest.itemData?.rows.length ? (
      <ListWrapper {...rest} />
    ) : (
      <TableEmptyMessage />
    );

    return (
      <div ref={ref} {...bodyProps} className={classNames(styles.tableBody, className)}>
        {loading ? <Loading /> : ListComponent}
      </div>
    );
  }
);

export default TableBody;
